// Content flow ([data-flow], "Was ist Ynfinite"): rose packets run from the source card into the Ynfinite card,
// which pulses, and from there fan out to the channels, which light up as their packet arrives. On wide screens
// (cards in a row) the SVG draws one curve from the Ynfinite card to every channel in place of the single CSS arrow;
// stacked, the packets follow the arrows down and the channels light up one after the other. A few rounds play
// each time the diagram comes into view, then it rests (WCAG 2.2.2: nothing keeps moving beside the text). The SVG
// is decoration (aria-hidden); the list stays the content. Skipped with reduced motion.
import { reducedMotion } from './motion.js'

const NS = 'http://www.w3.org/2000/svg'
const ROUNDS = 3
const WIDE = '(min-width: 52rem)' // the row layout of .flow (sections/_distribution.scss)

export function initFlow() {
	if (reducedMotion.matches) return
	document.querySelectorAll('[data-flow]').forEach(setupFlow)
}

function setupFlow(stage) {
	const svg = stage.querySelector('.flow-stage__lines')
	const source = stage.querySelector('.flow__card--source')
	const platform = stage.querySelector('.flow__card--platform')
	const list = stage.querySelector('.flow__channels ol')
	const channels = [...stage.querySelectorAll('.flow__channel')]
	if (!svg || !source || !platform || !list || !channels.length) return

	const wide = window.matchMedia(WIDE)
	let paths = { feed: null, fan: [] }
	let visible = false
	let playing = false

	const pathEl = (d, drawn) => {
		const path = document.createElementNS(NS, 'path')
		path.setAttribute('d', d)
		path.classList.add(drawn ? 'flow-stage__line' : 'flow-stage__track')
		svg.append(path)
		return path
	}

	// the paths from the current layout, relative to the stage
	const layout = () => {
		svg.replaceChildren()
		const box = stage.getBoundingClientRect()
		const rect = (el) => {
			const r = el.getBoundingClientRect()
			return { left: r.left - box.left, right: r.right - box.left, top: r.top - box.top, bottom: r.bottom - box.top, x: r.left + r.width / 2 - box.left, y: r.top + r.height / 2 - box.top }
		}
		svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`)
		const s = rect(source)
		const p = rect(platform)
		stage.classList.toggle('is-fanned', wide.matches)
		if (wide.matches) {
			paths = {
				feed: pathEl(`M${s.right} ${s.y}H${p.left}`),
				fan: channels.map((channel) => {
					const c = rect(channel)
					const bend = (c.left - p.right) / 2
					return pathEl(`M${p.right} ${p.y}C${p.right + bend} ${p.y} ${c.left - bend} ${c.y} ${c.left} ${c.y}`, true)
				}),
			}
		} else {
			const l = rect(list)
			paths = {
				feed: pathEl(`M${s.x} ${s.bottom}V${p.top}`),
				fan: [pathEl(`M${p.x} ${p.bottom}V${l.top}`)],
			}
		}
	}

	const flash = (el, name, ms) => {
		el.classList.add(name)
		setTimeout(() => el.classList.remove(name), ms)
	}

	// one packet along a path; resolves when it arrives
	const travel = (path, duration, delay = 0) =>
		new Promise((resolve) => {
			const dot = document.createElementNS(NS, 'circle')
			dot.setAttribute('r', '4.5')
			dot.classList.add('flow-stage__packet')
			const length = path.getTotalLength()
			let start = 0
			const step = (now) => {
				if (!start) {
					start = now
					svg.append(dot)
				}
				const t = Math.min(1, (now - start) / duration)
				const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
				const point = path.getPointAtLength(length * eased)
				dot.setAttribute('cx', point.x)
				dot.setAttribute('cy', point.y)
				if (t < 1) requestAnimationFrame(step)
				else {
					dot.remove()
					resolve()
				}
			}
			setTimeout(() => requestAnimationFrame(step), delay)
		})

	const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

	const round = async () => {
		flash(source, 'is-sending', 500)
		await travel(paths.feed, 650)
		flash(platform, 'is-pulse', 700)
		if (wide.matches) {
			await Promise.all(
				paths.fan.map((path, index) => travel(path, 750, index * 110).then(() => flash(channels[index], 'is-hit', 900))),
			)
		} else {
			await travel(paths.fan[0], 450)
			for (const channel of channels) {
				flash(channel, 'is-hit', 900)
				await wait(130)
			}
		}
	}

	const play = async () => {
		if (playing) return
		playing = true
		for (let i = 0; i < ROUNDS && visible; i++) {
			await round()
			await wait(900)
		}
		playing = false
	}

	layout()
	new ResizeObserver(layout).observe(stage)
	new IntersectionObserver(
		([entry]) => {
			visible = entry.isIntersecting
			if (visible) play()
		},
		{ threshold: 0.5 },
	).observe(stage)
}
